import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';

interface InfoOverlayProps {
  /** The trigger element ref (button/icon that opens the overlay) */
  triggerRef: React.RefObject<HTMLElement>;
  /** Whether the overlay is visible */
  isVisible: boolean;
  /** Callback when overlay should close */
  onClose: () => void;
  /** Content to display in the overlay */
  children: React.ReactNode;
  /** Optional width of the overlay (default: 22rem) */
  width?: string;
  /** Optional z-index (default: 2147483647 - maximum value) */
  zIndex?: number;
}

export const InfoOverlay: React.FC<InfoOverlayProps> = ({
  triggerRef,
  isVisible,
  onClose,
  children,
  width = '22rem',
  zIndex = 2147483647, // Maximum z-index value
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [chevronOffset, setChevronOffset] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    // Update position after overlay is rendered
    const updatePosition = () => {
      if (overlayRef.current && triggerRef.current) {
        const buttonRect = triggerRef.current.getBoundingClientRect();
        // Get the correct window (works in popouts)
        const targetWindow =
          triggerRef.current.ownerDocument?.defaultView ?? window;
        const viewportWidth = targetWindow.innerWidth;
        const overlayWidth = overlayRef.current.offsetWidth || 352; // ~22rem
        const padding = 16;
        const buttonCenter = buttonRect.left + buttonRect.width / 2;
        const desiredCenter = buttonCenter;
        const minCenter = padding + overlayWidth / 2;
        const maxCenter = viewportWidth - padding - overlayWidth / 2;
        const clampedCenter = Math.min(
          Math.max(desiredCenter, minCenter),
          maxCenter,
        );

        // Calculate chevron offset: difference between button center and overlay center
        const offset = buttonCenter - clampedCenter;
        setChevronOffset(offset);

        overlayRef.current.style.top = `${buttonRect.bottom + 8}px`;
        overlayRef.current.style.left = `${clampedCenter}px`;
      }
    };

    // Initial position update - use double RAF to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(updatePosition);
    });

    // Get the correct document and window (works in popouts)
    const targetDocument = triggerRef.current?.ownerDocument ?? document;
    const targetWindow =
      triggerRef.current?.ownerDocument?.defaultView ?? window;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        overlayRef.current &&
        triggerRef.current &&
        !overlayRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    targetDocument.addEventListener('mousedown', handleClickOutside);
    targetDocument.addEventListener('keydown', handleEscape);
    targetWindow.addEventListener('scroll', updatePosition, true);
    targetWindow.addEventListener('resize', updatePosition);

    return () => {
      targetDocument.removeEventListener('mousedown', handleClickOutside);
      targetDocument.removeEventListener('keydown', handleEscape);
      targetWindow.removeEventListener('scroll', updatePosition, true);
      targetWindow.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, onClose, triggerRef]);

  if (!isVisible) return null;

  // Get the correct document body (works in popouts)
  const targetBody = triggerRef.current?.ownerDocument?.body ?? document.body;

  return createPortal(
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        transform: 'translateX(-50%)',
        zIndex,
        width,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <div
        className="border-1 rounded-md shadow-lg p-3"
        style={{
          position: 'relative',
          backgroundColor: 'var(--bq-tooltip-bg)',
          borderColor: 'var(--bq-tooltip-border)',
        }}
      >
        {/* Chevron arrow - positioned to point at button center */}
        <div
          className="absolute"
          style={{
            top: '-8px',
            left: `calc(50% + ${chevronOffset}px)`,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: `8px solid var(--bq-tooltip-border)`,
            pointerEvents: 'none',
          }}
        />
        <div
          className="absolute"
          style={{
            top: '-7px',
            left: `calc(50% + ${chevronOffset}px)`,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: `8px solid var(--bq-tooltip-bg)`,
            pointerEvents: 'none',
          }}
        />
        <div className="text-sm" style={{ color: 'var(--bq-tooltip-text)' }}>
          {children}
        </div>
      </div>
    </div>,
    targetBody,
  );
};
